import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ApiClientError,
  type CreatedWebhook,
  type Delivery,
  type Instance,
  type Page,
  type Webhook,
} from '../api/types.js'
import { IconWebhooks } from '../icons.js'
import { Alert } from '../ui/Alert.js'
import { CopyButton } from '../ui/CopyButton.js'
import { ConfirmDialog, useConfirmation } from '../ui/ConfirmDialog.js'
import { useToaster } from '../ui/toasts.js'

/**
 * Узкий срез клиента: экрану нужны только операции над подписками и их
 * журналом доставок (барьер 2 — клиент API и есть граница процесса для
 * экранов админки, его двойник в тестах разрешён, см. Instances.tsx).
 */
export interface WebhooksClient {
  webhooks(): Promise<Webhook[]>
  createWebhook(url: string, events: string[], instanceId?: string | null): Promise<CreatedWebhook>
  removeWebhook(id: string): Promise<void>
  deliveries(webhookId: string): Promise<Page<Delivery>>
  instances(): Promise<Page<Instance>>
}

export interface WebhooksProps {
  client: WebhooksClient
  /** Права, выданные при входе (session.permissions()) — экран сам их не запрашивает. */
  permissions: string[]
  /**
   * Инстанс, к которому привязывается новая подписка. Задан — экран открыт
   * из карточки инстанса и показывает только его подписки (FR-WA-025).
   */
  instanceId?: string
}

const CAN_WRITE = 'webhooks.write'

/** Сообщение по умолчанию для отказов не от сервера (например, обрыв сети). */
const GENERIC_FAILURE = 'Не удалось выполнить действие. Проверьте соединение и попробуйте ещё раз.'

const DELIVERY_STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает',
  delivered: 'Доставлено',
  failed: 'Ошибка',
}

function deliveryStatusLabel(status: string): string {
  return DELIVERY_STATUS_LABELS[status] ?? status
}

/**
 * Разбирает поле формы «События» в список строк для сервера. Пустая строка
 * (и любые лишние запятые/пробелы вокруг элементов) даёт пустой массив —
 * сервер трактует его как подписку на все события (routes/webhooks.ts).
 */
function parseEvents(raw: string): string[] {
  return raw
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '')
}

export function Webhooks(props: WebhooksProps) {
  const { client, permissions } = props
  const canWrite = permissions.includes(CAN_WRITE)
  const toaster = useToaster()
  const удаление = useConfirmation<Webhook>()

  const [allWebhooks, setAllWebhooks] = useState<Webhook[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Форма создания подписки.
  const [showForm, setShowForm] = useState(false)
  const [url, setUrl] = useState('')
  const [eventsInput, setEventsInput] = useState('')
  // Область действия новой подписки: пусто — все инстансы. Открытый из
  // карточки инстанса экран сразу предлагает его.
  const [scope, setScope] = useState(props.instanceId ?? '')
  const [instances, setInstances] = useState<Instance[]>([])

  /*
   * Экран, открытый из карточки инстанса, показывает его подписки и общие:
   * общая тоже присылает его уведомления, и не показать её значит скрыть
   * половину ответа на вопрос «кто получает события этого номера».
   */
  const webhooks =
    allWebhooks === null || props.instanceId === undefined
      ? allWebhooks
      : allWebhooks.filter(
          (webhook) => webhook.instanceId === null || webhook.instanceId === props.instanceId,
        )

  /** Имя инстанса вместо идентификатора: его человек и выбирал. */
  const nameOfInstance = (id: string): string =>
    instances.find((instance) => instance.id === id)?.name ?? id
  const [creating, setCreating] = useState(false)
  // Ключ показывается только сразу после успешного создания — источник
  // правды не список подписок (там ключа нет и быть не должно, FR-SEC-013),
  // а именно ответ на POST, который сервер отдаёт один единственный раз.
  const [createdSecret, setCreatedSecret] = useState<CreatedWebhook | null>(null)

  // Подписки с удалением в процессе — блокируют повторное нажатие, тот же
  // приём, что у кнопок действий на экране инстансов (Instances.tsx).
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set())

  // Журнал доставок — свой раздел экрана, открывается отдельной кнопкой
  // и не привязан к конкретной строке списка: разбор отказов нужен и тогда,
  // когда подписок сейчас нет ни одной (например, ту, что отказывала, уже
  // удалили, а журнал всё ещё есть о чём посмотреть).
  const [journalOpen, setJournalOpen] = useState(false)
  const [journalWebhookId, setJournalWebhookId] = useState('')
  const [deliveries, setDeliveries] = useState<Delivery[] | null>(null)
  const [deliveriesError, setDeliveriesError] = useState<string | null>(null)

  // Какой подписке на самом деле принадлежит ответ, который вот-вот придёт.
  // При быстром переключении подписки в выпадающем списке запросы могут
  // разрешиться не в том порядке, в котором были отправлены; без сверки
  // с этим ref устаревший ответ по прежней подписке перезаписал бы уже
  // показанный журнал новой (тот же приём, что в Chats.tsx).
  const requestedWebhookIdRef = useRef('')

  const load = useCallback(async () => {
    try {
      const items = await client.webhooks()
      setAllWebhooks(items)
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    }
  }, [client])

  useEffect(() => {
    void load()
  }, [load])

  // Список инстансов нужен только форме — выбрать область действия.
  // Отказ здесь не мешает работать с подписками: останется «все инстансы».
  useEffect(() => {
    let cancelled = false
    client
      .instances()
      .then((page) => {
        if (!cancelled) setInstances(page.items)
      })
      .catch(() => {
        if (!cancelled) setInstances([])
      })
    return () => {
      cancelled = true
    }
  }, [client])

  const loadDeliveries = useCallback(
    async (webhookId: string) => {
      requestedWebhookIdRef.current = webhookId
      try {
        const page = await client.deliveries(webhookId)
        // Пока ответ летел, могли успеть выбрать другую подписку — тогда
        // этот ответ уже устарел, и применять его к экрану нельзя.
        if (requestedWebhookIdRef.current !== webhookId) return
        setDeliveries(page.items)
      } catch (cause) {
        if (requestedWebhookIdRef.current !== webhookId) return
        setDeliveriesError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
      }
    },
    [client],
  )

  // Пустой выбор при существующих подписках — не забытый параметр, а
  // незаконченное решение человека: адрес вида /webhooks//deliveries не
  // имеет смысла на настоящем сервере, и вести туда некого. Единственное
  // отступление — подписок нет вовсе (например, ту, что отказывала, уже
  // удалили): тогда запрос всё равно стоит сделать, журналу есть что
  // показать и без текущего списка подписок (FR-MSG-009).
  const needsWebhookSelection =
    journalOpen && webhooks !== null && webhooks.length > 0 && journalWebhookId === ''

  useEffect(() => {
    if (!journalOpen) return
    if (needsWebhookSelection) {
      setDeliveries(null)
      setDeliveriesError(null)
      return
    }
    setDeliveries(null)
    setDeliveriesError(null)
    void loadDeliveries(journalWebhookId)
  }, [journalOpen, journalWebhookId, needsWebhookSelection, loadDeliveries])

  function openJournalFor(webhookId: string) {
    setJournalWebhookId(webhookId)
    setJournalOpen(true)
  }

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (creating || url.trim() === '') return

    setCreating(true)
    setError(null)
    try {
      const created = await client.createWebhook(
        url.trim(),
        parseEvents(eventsInput),
        scope === '' ? null : scope,
      )
      setCreatedSecret(created)
      setUrl('')
      setEventsInput('')
      setShowForm(false)
      await load()
      toaster.succeeded(`Подписка на ${created.url} создана`)
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    } finally {
      setCreating(false)
    }
  }

  async function handleRemove(webhook: Webhook) {
    const id = webhook.id
    if (busy.has(id)) return
    удаление.cancel()
    setBusy((current) => new Set(current).add(id))
    setError(null)
    try {
      await client.removeWebhook(id)
      await load()
      toaster.succeeded(`Подписка на ${webhook.url} удалена`)
    } catch (cause) {
      setError(cause instanceof ApiClientError ? cause.message : GENERIC_FAILURE)
    } finally {
      setBusy((current) => {
        const next = new Set(current)
        next.delete(id)
        return next
      })
    }
  }

  return (
    <section aria-label="Подписки на уведомления">
      {error !== null && <Alert onDismiss={() => setError(null)}>{error}</Alert>}

      {createdSecret !== null && (
        <div className="secret-panel">
          <p>
            Ключ подписи: <code>{createdSecret.secret}</code>
          </p>
          <p role="alert">
            Скопируйте ключ сейчас — он больше не будет показан. Сервер хранит его
            зашифрованным и не может показать его снова; при утере ключ можно получить
            только пересозданием подписки.
          </p>
          <div className="btn-row">
            {/* Раз просим скопировать — даём чем (FR-ADM-036). */}
            <CopyButton value={createdSecret.secret} what="Ключ подписи" label="Скопировать ключ" />
            <button type="button" className="btn" onClick={() => setCreatedSecret(null)}>
              Ключ сохранён, закрыть
            </button>
          </div>
        </div>
      )}

      {canWrite && (
        <div className="card">
          <button type="button" className="btn btn--primary" onClick={() => setShowForm((value) => !value)}>
            Добавить подписку
          </button>

          {showForm && (
            <form className="form-inline" onSubmit={(event) => void handleCreate(event)}>
              <div className="field">
                <label htmlFor="webhook-url">Адрес</label>
                <input id="webhook-url" value={url} onChange={(event) => setUrl(event.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="webhook-events">События (через запятую, пусто — все)</label>
                <input
                  id="webhook-events"
                  value={eventsInput}
                  onChange={(event) => setEventsInput(event.target.value)}
                />
              </div>
              <div className="field">
                {/* Область действия: подписка на инстанс получает только его
                    уведомления, общая — все. У арендатора с десятком номеров
                    это разница в десять раз (FR-WA-025). */}
                <label htmlFor="webhook-instance">Номер</label>
                <select
                  id="webhook-instance"
                  value={scope}
                  onChange={(event) => setScope(event.target.value)}
                >
                  <option value="">Все номера</option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name}
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" className="btn btn--primary" disabled={creating}>
                Создать
              </button>
            </form>
          )}
        </div>
      )}

      <div className="card">
        <h2>Подписки</h2>
        {webhooks === null ? (
          <p>Загрузка подписок…</p>
        ) : webhooks.length === 0 ? (
          <div className="empty-state">
            <IconWebhooks />
            <p>Подписок пока нет — добавьте первую, чтобы начать получать уведомления.</p>
          </div>
        ) : (
          <ul className="entity-list">
            {webhooks.map((webhook) => {
              const rowBusy = busy.has(webhook.id)
              return (
                <li key={webhook.id} className="entity-row">
                  <p className="entity-row__title">
                    {webhook.url} —{' '}
                    <span className={`badge badge--${webhook.active ? 'ok' : 'neutral'}`}>
                      {webhook.active ? 'активна' : 'отключена'}
                    </span>
                    {webhook.events.length > 0 ? ` (${webhook.events.join(', ')})` : ' (все события)'}
                  </p>
                  {/* Область действия названа прямо: по адресу и событиям
                      не видно, весь ли это арендатор или один номер. */}
                  <p className="entity-row__meta">
                    {webhook.instanceId === null
                      ? 'Все номера'
                      : `Номер: ${nameOfInstance(webhook.instanceId)}`}
                  </p>
                  <div className="btn-row">
                    <button type="button" className="btn" onClick={() => openJournalFor(webhook.id)}>
                      Журнал доставок
                    </button>
                    {canWrite && (
                      <button
                        type="button"
                        className="btn btn--danger"
                        disabled={rowBusy}
                        onClick={() => удаление.ask(webhook)}
                      >
                        Удалить
                      </button>
                    )}
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <section aria-label="Журнал доставок" className="card">
        <div className="card__title">
          <h2>Журнал доставок</h2>
          <button type="button" className="btn" onClick={() => setJournalOpen((value) => !value)}>
            Доставки
          </button>
        </div>

        {journalOpen && (
          <>
            {webhooks !== null && webhooks.length > 0 && (
              <div className="field">
                <label htmlFor="journal-webhook">Подписка</label>
                <select
                  id="journal-webhook"
                  value={journalWebhookId}
                  onChange={(event) => setJournalWebhookId(event.target.value)}
                >
                  <option value="">Выберите подписку</option>
                  {webhooks.map((webhook) => (
                    <option key={webhook.id} value={webhook.id}>
                      {webhook.url}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {deliveriesError !== null && (
              <Alert onDismiss={() => setDeliveriesError(null)}>{deliveriesError}</Alert>
            )}

            {needsWebhookSelection ? (
              <p>Выберите подписку, чтобы посмотреть её журнал.</p>
            ) : deliveries === null ? (
              <p>Загрузка журнала доставок…</p>
            ) : deliveries.length === 0 ? (
              <p>Доставок пока нет — они появятся здесь после первой попытки отправить уведомление.</p>
            ) : (
              <ul className="entity-list">
                {deliveries.map((delivery) => (
                  <li key={delivery.id} className="entity-row">
                    <p className="entity-row__title">
                      {delivery.event} —{' '}
                      <span
                        className={`badge badge--${
                          delivery.status === 'delivered' ? 'ok' : delivery.status === 'failed' ? 'danger' : 'progress'
                        }`}
                      >
                        {deliveryStatusLabel(delivery.status)}
                      </span>{' '}
                      (попыток: {delivery.attempts})
                      {delivery.responseStatus !== null ? `, ответ сервера ${delivery.responseStatus}` : ''}
                    </p>
                    {/* Причина отказа — единственное, ради чего вообще открывают журнал (FR-MSG-009). */}
                    {delivery.lastError !== null && <Alert>{delivery.lastError}</Alert>}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {удаление.pending !== null && (
        <ConfirmDialog
          title={`Удалить подписку на ${удаление.pending.url}?`}
          confirmLabel="Удалить"
          busy={busy.has(удаление.pending.id)}
          onCancel={удаление.cancel}
          onConfirm={() => {
            if (удаление.pending !== null) void handleRemove(удаление.pending)
          }}
        >
          <p>
            Этот адрес перестанет получать события: входящие сообщения, статусы доставки и
            отказы. Система на другом конце не узнает об этом — она просто перестанет получать
            уведомления.
          </p>
          <p>Ключ подписи восстановить нельзя: новая подписка получит новый.</p>
        </ConfirmDialog>
      )}
    </section>
  )
}
